import React from 'react';
import { SynapticText } from '../hero/SynapticText';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';

const aboutFeatures = [
  {
    title: "Our Mission",
    description: "Every Voice Tells a Story - We're pioneering voice AI that transforms routine communications into health insights"
  },
  {
    title: "What We Do", 
    description: "Voice AI and speech biomarker analysis that makes screening accessible and effective for healthcare providers"
  },
  {
    title: "Science of Speech",
    description: "Research-backed speech pattern analysis using clinical research and machine learning to detect health changes"
  },
  {
    title: "Why This Matters",
    description: "Early detection saves lives. We're making cognitive and mental health screening part of every routine interaction"
  },
  {
    title: "Our Commitment",
    description: "Highest standards in healthcare technology with clinical validity, privacy protection, and universal accessibility"
  },
  {
    title: "Future of Healthcare",
    description: "Technology empowering human judgment where every interaction contributes to better patient outcomes"
  }
];

export const AboutFeaturesList = () => {
  return (
    <section className="relative py-32 overflow-hidden" style={{ background: 'var(--gradient-warm)' }}>
      {/* Neural Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 left-10 w-2 h-2 bg-primary rounded-full animate-ping"></div>
        <div className="absolute top-40 right-20 w-1 h-1 bg-accent rounded-full animate-pulse"></div>
        <div className="absolute bottom-32 left-1/4 w-1.5 h-1.5 bg-neural-violet rounded-full animate-ping"></div>
        <div className="absolute bottom-20 right-1/3 w-1 h-1 bg-primary rounded-full animate-pulse"></div>
        <div className="absolute top-1/2 left-1/2 w-1 h-1 bg-accent rounded-full animate-ping"></div>
      </div>

      {/* Background Neural Connections */}
      <div className="absolute inset-0 pointer-events-none opacity-20">
        <svg className="w-full h-full" viewBox="0 0 1200 800">
          <defs>
            <linearGradient id="neural-gradient-about" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.4" />
              <stop offset="50%" stopColor="hsl(var(--accent))" stopOpacity="0.3" />
              <stop offset="100%" stopColor="hsl(var(--neural-violet))" stopOpacity="0.4" />
            </linearGradient>
          </defs>
          <path d="M100,150 Q300,100 500,150 T900,200" stroke="url(#neural-gradient-about)" strokeWidth="1" fill="none" className="animate-pulse" />
          <path d="M150,300 Q350,250 550,300 T950,350" stroke="url(#neural-gradient-about)" strokeWidth="1" fill="none" className="animate-pulse" />
          <path d="M200,450 Q400,400 600,450 T1000,500" stroke="url(#neural-gradient-about)" strokeWidth="1" fill="none" className="animate-pulse" />
          <path d="M250,600 Q450,550 650,600 T1050,650" stroke="url(#neural-gradient-about)" strokeWidth="1" fill="none" className="animate-pulse" />
        </svg>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Neural Section Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-3 mb-8 px-4 py-2 bg-primary/10 rounded-full border border-primary/20">
            <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
            <span className="text-sm font-mono text-blue-400 uppercase tracking-wider">About_Network_Active</span>
          </div>
          
          <h2 className="heading-display text-4xl sm:text-5xl lg:text-6xl mb-8 text-white">
            <SynapticText 
              text="Every Voice" 
              delay={100}
              className="block text-white"
            />
            <SynapticText 
              text="Tells a Story" 
              delay={600}
              className="block text-blue-400 mt-2"
            />
          </h2>
        </div>
        
        {/* Neural Feature List */}
        <div className="space-y-16 max-w-4xl mx-auto">
          {aboutFeatures.map((feature, index) => (
            <div key={index} className="relative">
              {/* Neural Connection Node */}
              <div className="absolute -left-8 top-6">
                <div className="w-3 h-3 bg-primary rounded-full animate-pulse"></div>
                <div className="absolute inset-0 w-3 h-3 bg-primary rounded-full animate-ping opacity-50"></div>
              </div>
              
              {/* Feature Content */}
              <div className="pl-8 border-l border-primary/20">
                <div className="mb-6">
                  <h3 className="heading-primary text-2xl sm:text-3xl mb-4">
                    <SynapticText 
                      text={feature.title}
                      delay={3000 + (index * 800)}
                      className="text-white group-hover:text-blue-400 transition-colors"
                    />
                  </h3>
                </div>
                
                <div className="text-lg sm:text-xl text-white/70 leading-relaxed">
                  <SynapticText 
                    text={feature.description}
                    delay={4000 + (index * 800)}
                    className="font-mono text-base text-white/80"
                  />
                </div>
                
                {/* Neural Activity Indicator */}
                <div className="mt-6 flex items-center gap-3">
                  <div 
                    className="w-0 h-0.5 bg-gradient-to-r from-primary to-accent transition-all duration-1000"
                    style={{ 
                      width: '48px',
                      animationDelay: `${5000 + (index * 800)}ms`,
                      animation: 'expand-line 0.8s ease-out forwards'
                    }}
                  ></div>
                  <div 
                    className="w-2 h-2 bg-accent rounded-full opacity-0"
                    style={{ 
                      animationDelay: `${5800 + (index * 800)}ms`,
                      animation: 'fade-in-pulse 0.5s ease-out forwards'
                    }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* CTA Section */}
        <div className="text-center mt-20">
          <Button 
            size="lg" 
            className="btn-cta btn-xl group relative overflow-hidden"
            onClick={() => window.open('https://calendly.com/david-scienzahealth/product-demo', '_blank')}
          >
            <span className="relative z-10 flex items-center justify-center">
              Partner with Us
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </span>
          </Button>
        </div>
        
      </div>
    </section>
  );
};